import { deriveActivityDefinitionFields } from "../fhir/collections";
/**
 * Turn an as-of query result into the SAME DTO the live hook returns, so a past
 * view renders through the existing read components unchanged.
 *
 * The whole design rests on one thing being true, and it is: every catalog
 * projection is already a pure function over a resource (or, for the two
 * composed kinds, over a plain object holding a resource and its children).
 * None of them needs a TanStack DB collection. So a past view does not need a
 * second projection, a server-side port, or a live query — it needs the right
 * resources handed to the projection that already exists.
 *
 * Two families:
 *
 *   ROOT-ONLY — finding, exam, device, treatment, diagnostic. One resource in,
 *   one DTO out. `include: [root]`, a few KB, no composition.
 *
 *   COMPOSED — protocol, program. The projection reads the parent's own actions
 *   plus its children's headers, so `include` is the whole reachable set.
 *
 * All eight kinds with a version history are covered.
 */

import type {
  ActivityDefinition,
  DeviceDefinition,
  ObservationDefinition,
  PlanDefinition,
  Questionnaire,
} from "@medplum/fhirtypes";
import { catalogModelKindOf, type ResolveDefinitionAsOfResult } from "@repo/api";
import { derivePlanDefinitionFields } from "../fhir/collections";
import { deriveDeviceDefinitionFields } from "../fhir/device-definition-derived";

import type { ActivityDefinitionRow, DeviceDefinitionRow, PlanDefinitionRow } from "../fhir/types";
import { projectDeviceDefinitionRow } from "../views/device-definition-projection";
import { projectDiagnosticDefinitionRow } from "../views/diagnostic-definition-projection";
import { projectExamDefinition } from "../views/exam-definition";
import { projectFindingDefinition } from "../views/finding-definition";
import { projectPerformanceMeasureDefinition } from "../views/performance-measure-definition";
import { projectProgramDefinitionRow } from "../views/program-definition-projection";
import { projectProtocolDefinitionDetail } from "../views/protocol-definition-detail";
import { projectQuestionnaireDefinition } from "../views/questionnaire-definition";
import { projectTreatmentDefinitionRow } from "../views/treatment-definition-projection";

/** The kinds whose past state this can render today. */
export type ProjectableKind =
  | "finding"
  | "exam"
  | "device"
  | "treatment"
  | "diagnostic"
  | "performance-measure"
  | "questionnaire"
  | "protocol"
  | "program";

/**
 * What to pass as `include`. Root-only kinds ask for one resource; composed
 * kinds ask for the reachable set, which is why the caller needs the shape
 * before it can ask for the content — a `changePoints`-style cheap call first,
 * then this.
 */
export function includeForKind(kind: ProjectableKind, definitionUrl: string, reachable: readonly string[]): string[] {
  return kind === "protocol" || kind === "program" ? [...reachable] : [definitionUrl];
}

function activityRow(resource: ActivityDefinition): ActivityDefinitionRow {
  // `catalogModelKind` is the discriminator the composed projection branches on
  // (diagnostic vs treatment), and `catalogVersion` orders revisions. Both are
  // derived rather than stored, so they are recomputed here exactly as the
  // collection's `deriveFields` would.
  return {
    ...resource,
    catalogModelKind: catalogModelKindOf(resource),
    catalogVersion: Number(resource.version ?? "0"),
  } as ActivityDefinitionRow;
}

/**
 * The DTO for whatever kind this result's root is, or undefined when the root
 * did not resolve at that instant — a lineage that did not exist yet is an
 * answer, not a failure.
 */
export function projectDefinitionAsOf(result: ResolveDefinitionAsOfResult): unknown {
  const root = result.resources[result.definitionUrl];
  if (!root) return undefined;

  switch (result.tree.kind) {
    case "finding":
      return projectFindingDefinition(root as ObservationDefinition);
    case "exam":
      return projectExamDefinition(root as ActivityDefinition);
    case "device":
      return projectDeviceDefinitionRow(root as DeviceDefinition);
    case "treatment":
      return projectTreatmentDefinitionRow(root as ActivityDefinition);
    case "diagnostic":
      return projectDiagnosticDefinitionRow(root as ActivityDefinition);
    case "performance-measure":
      return projectPerformanceMeasureDefinition(root as Questionnaire);
    case "questionnaire":
      return projectQuestionnaireDefinition(root as Questionnaire);
    case "protocol":
      return projectProtocolDefinitionDetail(composeProtocolRow(result, root as PlanDefinition));
    case "program":
      // A program's sessions name protocols, and it needs only their headers —
      // the projection reads each one's name and total duration.
      return projectProgramDefinitionRow(
        root as PlanDefinition,
        childResources(result).filter(
          (resource): resource is PlanDefinition => resource.resourceType === "PlanDefinition",
        ),
      );
    default:
      return undefined;
  }
}

/** Everything the graph reached except the root itself. */
function childResources(result: ResolveDefinitionAsOfResult) {
  return Object.entries(result.resources)
    .filter(([lineage]) => lineage !== result.definitionUrl)
    .map(([, resource]) => resource);
}

/**
 * Rebuild the composed row the protocol projection expects.
 *
 * The children are bucketed by resource type rather than by the tree, because
 * the projection indexes them by url and does not care about position. The
 * parent's own actions carry the occurrences, durations and parameter values;
 * the children contribute only their headers.
 */
function composeProtocolRow(result: ResolveDefinitionAsOfResult, root: PlanDefinition) {
  const children = childResources(result);
  return {
    ...root,
    ...derivePlanDefinitionFields(root),
    activityDefinitions: children
      .filter((resource) => resource.resourceType === "ActivityDefinition")
      .map((resource) => activityRow(resource as ActivityDefinition)),
    deviceDefinitions: children
      .filter((resource) => resource.resourceType === "DeviceDefinition")
      .map((resource) => ({
        ...(resource as DeviceDefinition),
        ...deriveDeviceDefinitionFields(resource as DeviceDefinition),
      })) as DeviceDefinitionRow[],
    examDefinitions: children
      .filter((resource) => resource.resourceType === "ActivityDefinition")
      .map((resource) => ({
        ...(resource as ActivityDefinition),
        ...deriveActivityDefinitionFields(resource as ActivityDefinition),
      })) as ActivityDefinitionRow[],
  } as PlanDefinitionRow & {
    activityDefinitions: ActivityDefinitionRow[];
    deviceDefinitions: DeviceDefinitionRow[];
    examDefinitions: ActivityDefinitionRow[];
  };
}
